import { useEffect, useRef, useState, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

interface Stat {
  value: number;
  labelKey: string;
  suffix?: string;
  prefix?: string;
  format?: "currency" | "number";
}

const useCountUp = (target: number, duration: number, isVisible: boolean, delay: number = 0) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!isVisible) return;
    
    const timeout = setTimeout(() => {
      let startTime: number | null = null;
      
      const easeOutQuad = (t: number) => t * (2 - t);
      
      const animate = (currentTime: number) => {
        if (startTime === null) startTime = currentTime;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        setCount(Math.floor(target * easeOutQuad(progress)));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
    }, delay);
    
    return () => clearTimeout(timeout);
  }, [isVisible, target, duration, delay]);
  
  return count;
};

const formatNumber = (num: number, format?: "currency" | "number"): string => {
  if (format === "currency") {
    return num.toString();
  }
  return num.toLocaleString("en-US");
};

const StatsSection = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const stats: Stat[] = [
    { value: 124000, labelKey: "stats.revenue", prefix: "$", suffix: "+", format: "currency" },
    { value: 5, labelKey: "stats.experience", suffix: "+" },
    { value: 1100, labelKey: "stats.creatives", suffix: "+" },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 md:py-24 px-4 md:px-6 relative">
      <div className="absolute inset-0 grid-pattern opacity-50" />
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-12 md:mb-16 text-center text-glow-hover uppercase">
          {t("stats.title")} <span className="text-holographic">{t("stats.numbers")}</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 px-4 overflow-visible">
          {stats.map((stat, index) => (
            <StatCard 
              key={index} 
              stat={stat} 
              isVisible={isVisible} 
              delay={index * 200} 
            />
          ))}
        </div>
      </div>
    </section>
  );
};

interface StatCardProps {
  stat: Stat;
  isVisible: boolean;
  delay: number;
}

const StatCard = ({ stat, isVisible, delay }: StatCardProps) => {
  const { t } = useLanguage();
  const count = useCountUp(stat.value, 4000, isVisible, delay);
  
  return (
    <div
      className={`p-6 md:p-8 text-center transition-all duration-500 overflow-visible ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-holographic mb-3 md:mb-4">
        {stat.prefix}
        {formatNumber(count, stat.format)}
        {stat.suffix}
      </div>
      <div className="text-sm sm:text-base md:text-lg text-muted-foreground uppercase tracking-wider">
        {t(stat.labelKey)}
      </div>
    </div>
  );
};

export default StatsSection;
